import {
  worstOutcomeBySession,
  type PostOutcomeStatus,
  type TimeBillPostResult,
} from '@worklogix/shared';

/**
 * Pure mapping from a NetSuite post response's per-entry outcomes to the badge
 * descriptors surfaced per session in Hours & Timesheet (plan-v8 WEB #4).
 *
 * The server returns a `posted` / `skipped` / `failed` status plus a NON-PII
 * message per entry (Dev-B contract: names/PINs scrubbed; e.g. "unmapped
 * employee", "NetSuite auth failed", "NetSuite validation error"). This module
 * turns that into a Fluent Badge color + label + tooltip WITHOUT inventing or
 * leaking any detail — it trusts the server's non-PII message when present and
 * otherwise falls back to a safe generic line per status.
 */

/** The post outcome we render a badge for. Re-exported so callers here need one import. */
export type { PostOutcomeStatus };

/** A rendered post-outcome badge descriptor. */
export interface PostOutcomeBadge {
  status: PostOutcomeStatus;
  /** Short badge label. */
  label: string;
  /** Fluent Badge `color`. */
  color: 'success' | 'warning' | 'danger';
  /** Non-PII tooltip/explanation. */
  tooltip: string;
}

const DEFAULT_TOOLTIP: Record<PostOutcomeStatus, string> = {
  posted: 'Posted to NetSuite as a TimeBill entry.',
  skipped:
    'Not sent to NetSuite — the session is unmapped or ineligible (e.g. missing NetSuite employee, department, or payroll item). Complete the mapping, then re-post.',
  failed:
    'NetSuite rejected this entry. It was not posted and can be retried after the underlying issue is resolved.',
};

const LABEL: Record<PostOutcomeStatus, string> = {
  posted: 'Posted',
  skipped: 'Skipped',
  failed: 'Failed',
};

const COLOR: Record<PostOutcomeStatus, PostOutcomeBadge['color']> = {
  posted: 'success',
  skipped: 'warning',
  failed: 'danger',
};

/**
 * Build a badge descriptor for one post outcome. Uses the server's non-PII
 * `message` as the tooltip when it is a non-empty string; otherwise the safe
 * per-status default. Never surfaces raw ids beyond what the server chose to send.
 */
export function postOutcomeBadge(status: PostOutcomeStatus, message?: string): PostOutcomeBadge {
  const trimmed = message?.trim();
  return {
    status,
    label: LABEL[status],
    color: COLOR[status],
    tooltip: trimmed !== undefined && trimmed !== '' ? trimmed : DEFAULT_TOOLTIP[status],
  };
}

/**
 * Index a post response's per-entry results into a `sessionId → badge` map so each
 * affected session row can show its outcome immediately, before the list is reloaded.
 *
 * The "which outcome wins" rule lives in @worklogix/shared now — the API writes the
 * SAME decision onto the session, and two copies of it would eventually disagree about
 * a split day where one share posted and another failed.
 */
export function indexPostResults(
  results: readonly TimeBillPostResult[]
): Map<string, PostOutcomeBadge> {
  const map = new Map<string, PostOutcomeBadge>();
  for (const [sessionId, outcome] of worstOutcomeBySession(results)) {
    map.set(sessionId, postOutcomeBadge(outcome.status, outcome.message ?? undefined));
  }
  return map;
}
